import { cn } from "@/lib/utils";
import { Icon } from "./Icon";

/**
 * Semáforo de la cifra. Es un JUICIO sobre el dato, no decoración: sólo se
 * pasa cuando el KPI tiene un umbral de negocio que decide si el número está
 * bien o mal. Un KPI puramente informativo (una fecha, un total) se deja sin
 * `tono` y hereda el color de texto normal.
 *
 * - `exito`: el umbral se cumple.
 * - `advertencia`: hay algo que atender, sin ser una falla.
 * - `critico`: el umbral se incumple.
 * - `neutro`: no hay dato todavía sobre el cual emitir un juicio (el KPI
 *   existe pero está vacío) — se apaga en gris en vez de pintarse de verde,
 *   que leería como "todo en orden".
 */
export type TonoKpi = "exito" | "advertencia" | "critico" | "neutro";

const TONO_KPI: Record<TonoKpi, string> = {
  exito: "text-primary",
  advertencia: "text-warning",
  critico: "text-destructive",
  neutro: "text-soft",
};

export interface KpiCardProps {
  /** Qué se está midiendo. */
  label: string;
  /** La cifra. */
  valor: React.ReactNode;
  /**
   * OBLIGATORIO: explica el denominador — sobre qué se calculó la cifra
   * ("71 de 77 destinatarios", "aprueba con 4,0"). Un KPI sin denominador
   * explícito no se puede reconciliar con ninguna otra pantalla.
   */
  detalle: string;
  /** Marca la cifra en rojo cuando el KPI es una alerta. */
  alerta?: boolean;
  /**
   * Semáforo completo de la cifra. Si se pasa, MANDA sobre `alerta` — los dos
   * dicen lo mismo con distinto alcance y `alerta` es el caso binario
   * (normal/rojo) que ya usan varias pantallas.
   */
  tono?: TonoKpi;
  /**
   * Glifo decorativo arriba a la derecha (Material Symbols). Opcional: sólo
   * vale la pena cuando hay varias tarjetas juntas que se escanean de un
   * vistazo, como en el Dashboard. No aporta información — el dato es el
   * label — así que va en gris de ícono decorativo.
   */
  icono?: string;
  /** Si se pasa, la tarjeta se comporta como botón (p.ej. filtra la tabla de abajo). */
  onClick?: () => void;
  className?: string;
}

/**
 * Tarjeta de KPI del Design System: label 12px → cifra 24px/600 → subtexto
 * 11px con el denominador. Con `onClick` se renderiza como `<button>` — el
 * mismo patrón se repite en varias pantallas (tarjetas resumen que filtran
 * la tabla que tienen debajo).
 */
export function KpiCard({
  label,
  valor,
  detalle,
  alerta = false,
  tono,
  icono,
  onClick,
  className,
}: KpiCardProps) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "bg-card flex flex-col gap-1.5 rounded-xl border px-4 py-3.5 text-left",
        onClick && "hover:border-primary-border cursor-pointer transition-colors",
        className
      )}
    >
      <span className="flex items-center gap-2">
        <span className="text-muted-foreground fc-truncate min-w-0 flex-1 text-xs">{label}</span>
        {icono && <Icon name={icono} size={16} className="text-icon-soft" />}
      </span>
      <span
        className={cn(
          "fc-kpi-value",
          tono ? TONO_KPI[tono] : alerta ? "text-destructive" : "text-foreground"
        )}
      >
        {valor}
      </span>
      <span className="text-soft text-[11px] leading-[1.35]">{detalle}</span>
    </Comp>
  );
}

/** Grilla de KPIs: envuelve, nunca se comprime en columnas fijas. */
export function KpiGrid({
  className,
  min = 196,
  ...props
}: React.ComponentProps<"div"> & { min?: number }) {
  return (
    <div
      className={cn("grid gap-3", className)}
      style={{
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
      }}
      {...props}
    />
  );
}
